import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { userService } from '../../../services/userService';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

interface User {
    id: number;
    name: string;
    lastname: string;
    email: string;
    username?: string | null;
    status: 'A' | 'I';
    last_session?: string;
    profile?: { id: number; name: string };
    roles?: Array<{ id: number; name: string }>;
}

const UserListPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [total, setTotal] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [toggling, setToggling] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await userService.getAll({
                per_page: 50,
                search: search || undefined,
                status: statusFilter || undefined,
            });
            setUsers((result.data ?? []) as User[]);
            setTotal(result.total ?? 0);
        } catch {
            setError('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter]);

    useEffect(() => { load(); }, [load]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        load();
    };

    const handleToggle = async (user: User) => {
        setToggling(user.id);
        try {
            await userService.toggleStatus(user.id);
            setUsers(prev => prev.map(u =>
                u.id === user.id ? { ...u, status: u.status === 'A' ? 'I' : 'A' } : u
            ));
        } catch {
            setError('Error al cambiar estado');
        } finally {
            setToggling(null);
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/admin-dashboard' }, { label: 'Usuarios' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="card-title mb-0">
                                    <i className="fa fa-users me-2"></i>Usuarios ({total})
                                </h4>
                                <Link to="/admin/users/new" className="btn btn-primary btn-sm">
                                    <i className="fa fa-plus me-1"></i> Nuevo Usuario
                                </Link>
                            </div>

                            <form onSubmit={handleSearch} className="row g-2 mb-3">
                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Buscar por nombre, usuario o email..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select form-select-sm"
                                        value={statusFilter}
                                        onChange={e => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">Todos los estados</option>
                                        <option value="A">Activos</option>
                                        <option value="I">Inactivos</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary btn-sm w-100">
                                        <i className="fa fa-search me-1"></i> Buscar
                                    </button>
                                </div>
                            </form>

                            {error && <div className="alert alert-danger">{error}</div>}

                            {loading ? (
                                <Spinner variant="full-page" text="Cargando..." size="md" />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-center mb-0">
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Usuario</th>
                                                <th>Email</th>
                                                <th>Perfil</th>
                                                <th>Roles</th>
                                                <th>Última sesión</th>
                                                <th>Estado</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.length === 0 ? (
                                                <EmptyState variant="compact" icon="fa-users" title="No hay usuarios registrados" action={{ label: 'Crear usuario', to: '/admin/users/new', icon: 'fa-plus' }} />
                                            ) : users.map(u => (
                                                <tr key={u.id}>
                                                    <td>
                                                        <strong>{u.name} {u.lastname}</strong>
                                                    </td>
                                                    <td>{u.username || <span className="text-muted small">—</span>}</td>
                                                    <td>{u.email}</td>
                                                    <td>
                                                        {u.profile
                                                            ? <span className="badge bg-info">{u.profile.name}</span>
                                                            : <span className="text-muted small">Sin perfil</span>}
                                                    </td>
                                                    <td>
                                                        {(u.roles ?? []).map(r => (
                                                            <span key={r.id} className="badge bg-secondary me-1">{r.name}</span>
                                                        ))}
                                                    </td>
                                                    <td className="text-muted small">
                                                        {u.last_session
                                                            ? new Date(u.last_session).toLocaleDateString('es-DO')
                                                            : 'Nunca'}
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${u.status === 'A' ? 'success' : 'danger'}`}>
                                                            {u.status === 'A' ? 'Activo' : 'Inactivo'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Link
                                                                to={`/admin/users/${u.id}/edit`}
                                                                className="btn btn-sm bg-warning-light"
                                                                title="Editar"
                                                            >
                                                                <i className="fa fa-edit"></i>
                                                            </Link>
                                                            <button
                                                                className={`btn btn-sm ${u.status === 'A' ? 'bg-danger-light' : 'bg-success-light'}`}
                                                                onClick={() => handleToggle(u)}
                                                                disabled={toggling === u.id}
                                                                title={u.status === 'A' ? 'Deshabilitar' : 'Habilitar'}
                                                            >
                                                                {toggling === u.id
                                                                    ? <Spinner size="sm" />
                                                                    : <i className={`fa fa-${u.status === 'A' ? 'ban' : 'check'}`}></i>}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserListPage;
